import { useQuery } from "@tanstack/react-query";
import moment from "moment";

const AUTH_TOKEN = "a669a058-4284-4eb0-9f14-36e717ed04d8";

const API_BASE_URL =
  process.env.REACT_APP_ENV === "production"
    ? "https://itpiapi.lancaster.ac.uk/transport"
    : "https://jedha.lancaster.ac.uk/transport";

export type BusStop = {
  atcoCode: string;
  gridType: string;
  easting: string;
  northing: string;
  latitude: string;
  longitude: string;
  commonName: string;
  identifier: string;
  direction: string | null;
  street: string;
  landmark: string;
  town: string;
  suburb: string;
  stopType: string;
  busStopType: string | null;
  busRegistrationStatus: string | null;
  notes: string;
  recordStatus: string;
  localityCentre: string;
  smsNumber: string;
  lastChanged: string;
};

export type BusLocation = {
  bearing: string;
  latitude: number;
  longitude: number;
  vehicleRef: string;
};

export type BusTime = {
  smscode: null | string;
  destinationRef: string;
  atcocode: string;
  mode: null | string;
  line: string;
  direction: string;
  directionLocality: null | string;
  operator: string;
  aimedArrivalTime: string;
  expectedArrivalTime: string;
  aimed_departure_time: string;
  aimedDepartureTime: string;
  expectedDepartureTime: string;
  departureStatus: null | string;
  source: string;
  dir: string;
  date: null | string;
  stand: string;
  isMonitored: boolean;
  vehicleFeatureRef: null | string;
  destinationName: string;
  vehicleLocation: null | BusLocation;
  sourceAtcoCode?: string;
};

export const useNearbyBusStops = (lng?: number, lat?: number) => {
  return useQuery<BusStop[]>(
    ["Location", lat, lng],
    async () => {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/BusStops?longitude=${lng}&latitude=${lat}&token=${AUTH_TOKEN}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok)
        throw new Error(
          "Could not find nearby bus stops. Please try again later."
        );
      return response.json();
    },
    { enabled: lat !== undefined && lng !== undefined }
  );
};

export const useBusSearch = (query: string) => {
  return useQuery<BusStop[]>(
    ["SearchBusStops", query],
    async () => {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/BusStops/?q=${query}&town=Lancaster&token=${AUTH_TOKEN}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok)
        throw new Error("Could not find bus stops. Please try again later.");
      return response.json();
    },
    { enabled: query.length > 0 }
  );
};

export const useAllBusStops = () => {
  return useQuery<BusStop[]>(["GetAllBusStops"], async () => {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/BusStops/?town=Lancaster&token=${AUTH_TOKEN}`,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    );
    if (!response.ok)
      throw new Error(
        "Could not get information on this bus stop. Please try again later."
      );
    const data = await response.json();
    return data;
  });
};

export const getBusTimes = async (atcoCode: string): Promise<BusTime[]> => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/BusStops/${atcoCode}/next?includeLocation=true&token=${AUTH_TOKEN}`,
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok)
    throw new Error("Could not get bus times. Please try again later.");
  return response.json();
};

export const useBusTimes = (atcoCode?: string) => {
  return useQuery<BusTime[]>(
    ["BusTimes", atcoCode],
    () => getBusTimes(atcoCode!),
    {
      refetchInterval: 30000,
      enabled: !!atcoCode,
    }
  );
};

export const getBusStop = async (atcoCode: string) => {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/BusStops/${atcoCode}?token=${AUTH_TOKEN}`
  );
  if (!response.ok)
    throw new Error(
      "Could not get information on this bus stop. Please try again later."
    );
  const stops: (BusStop | null)[] = await response.json();
  if (stops.length > 0 && stops[0]) {
    return stops[0];
  } else {
    throw new Error(
      "Could not get information on this bus stop. Please try again later."
    );
  }
};

export const useBusStop = (atcoCode?: string) => {
  return useQuery<BusStop>(["BusStop", atcoCode], () => getBusStop(atcoCode!), {
    enabled: atcoCode !== undefined,
  });
};

export type UseBusRouteOptions = {
  atcoCode: string;
  aimedDepartureTime: string;
  operator: string;
  line: string;
  dir: string;
};

export const useBusRoute = (busTime: Partial<UseBusRouteOptions>) => {
  return useQuery<BusTime[]>(
    ["GetBusRoute", busTime],
    async () => {
      const departureTime = busTime.aimedDepartureTime
        ? moment(busTime.aimedDepartureTime).format("YYYY-MM-DDTHH:mm:ss")
        : null;

      const response = await fetch(
        `${API_BASE_URL}/api/v1/BusStops/${busTime.atcoCode}/route/${busTime.operator}/${busTime.line}/${busTime.dir}/${departureTime}?token=${AUTH_TOKEN}`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok)
        throw new Error(
          "Could not get information for this bus route. Please try again later."
        );
      if (response.status === 204)
        throw new Error(
          "Could not get information for this bus route. Please try again later."
        );
      return response.json();
    },
    {
      enabled:
        !!busTime.aimedDepartureTime &&
        !!busTime.atcoCode &&
        !!busTime.operator &&
        !!busTime.line &&
        !!busTime.dir,
    }
  );
};

export const useAllBusLocations = () => {
  return useQuery<BusTime[]>(
    ["GetAllBusLocations"],
    async () => {
      const underpass = await getBusTimes("2500918");
      const busStation = await getBusTimes("LANCBS001");
      const commonGardenSt = await getBusTimes("LANCBS002");
      
      underpass.forEach(bus => bus.sourceAtcoCode = "2500918");
      busStation.forEach(bus => bus.sourceAtcoCode = "LANCBS001");
      commonGardenSt.forEach(bus => bus.sourceAtcoCode = "LANCBS002");

      return [...underpass, ...busStation, ...commonGardenSt];
    },
    { refetchInterval: 10000 }
  );
};

export const createBusSVG = (route: string, bearing: number | null) => {
  const busSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 430 430">
      <g transform="rotate(${bearing ? bearing : 0} 230 184.93)">
        <circle fill="#FFFFFF" style="stroke:#006382; stroke-miterlimit:10; stroke-width:12px;" cx="230" cy="184.93" r="137.78" />
        ${
          bearing
            ? `<path fill="#006382" d="M230.7,0,167.85,57.84s50.73-12.57,66.44-12.57,59.26,12.57,59.26,12.57Z" />`
            : null
        }
      </g>
    
      <path fill="#006382" d="M153.3,145.34c0-4.51-2.11-6.54-6.57-6.5s-6.5,2.32-6.46,6.7c0,3.59,0,7.18,0,10.77,0,4.07-.08,8.14,0,12.21s2.77,5.87,6.62,5.82,6.36-1.71,6.34-6C153.23,160.66,153.23,153,153.3,145.34Zm161.46-6.5c-5-.08-6.77,2.45-6.68,7.13.11,7.17.17,14.36,0,21.54-.1,4.49,1.93,6.81,6.29,6.86,4.72.06,7-2.4,6.74-7.2-.15-3.34,0-6.7,0-10,0-3.59-.08-7.19,0-10.77C321.25,141.88,319.92,138.92,314.76,138.84Z" />
      <path fill="#006382" d="M257.71,255.61h0c.29-.46.56-.91.79-1.34C258.26,254.7,258,255.15,257.71,255.61Z" />
      <path fill="#006382" d="M321.1,157.12c0,3.35-.13,6.71,0,10,.22,4.8-2,7.26-6.74,7.2-4.36-.05-6.38-2.37-6.29-6.86.16-7.18.11-14.36,0-21.54-.08-4.68,1.69-7.22,6.7-7.14s6.48,3,6.35,7.52C321,149.94,321.1,153.53,321.1,157.12Z" />
      <path fill="#006382" d="M140.28,156.31c0-3.59,0-7.18,0-10.77,0-4.37,1.93-6.67,6.46-6.7s6.62,2,6.57,6.5q-.11,11.49,0,23c0,4.32-2.47,6-6.34,6s-6.51-1.6-6.62-5.83C140.21,164.45,140.29,160.38,140.28,156.31Z" />
      <path fill="#006382" d="M258.51,254.27c-.23.43-.5.88-.79,1.34h0C258,255.15,258.26,254.7,258.51,254.27Z" />
      <path fill="#006382" d="M303.81,124.11c0-7-3-12.41-8.31-16.71a52.09,52.09,0,0,0-17.05-8.87c-24.88-8.06-50.25-8.67-75.86-4.79-11.4,1.72-22.44,4.6-32.45,10.63-8.44,5.08-12.86,12.09-12.63,22.41.43,18.88.13,37.8.13,56.69,0,19.62.08,39.24,0,58.85,0,6.48,1.53,12.74,7.58,15,6.5,2.46,6.93,6.55,6.45,12-.56,6.39,2.43,9.11,8.84,9.11,10.27,0,11.9-1.11,11.54-11.32-.2-5.48,1.13-7.35,7-7.25,20.57.4,41.16.45,61.72,0,6.79-.15,9.25,1.33,8.69,8.5-.66,8.49,1.36,10,9.86,10,9.11,0,10.61-1.31,10.47-10.27-.07-4.65-.13-8.17,5.71-10.24,6-2.14,8.34-8.05,8.33-14.6Q303.74,183.69,303.81,124.11Zm-97.46-14.24q24.3.34,48.61,0c5-.08,6.7,1.74,6.7,6.63s-1.42,7.43-6.94,7.19c-8.09-.38-16.2-.1-24.3-.1s-16.21-.22-24.3.08c-5.37.2-6.31-2.5-6.34-7C199.73,111.88,201.22,109.8,206.35,109.87ZM182.56,246.69A12.28,12.28,0,0,1,170.73,234c.4-6.29,6.36-11.72,12.49-11.39A12.52,12.52,0,0,1,194.71,235,12.38,12.38,0,0,1,182.56,246.69Zm77,4.82a2.77,2.77,0,0,1-.11.56v0a2.29,2.29,0,0,1-.12.37,9.86,9.86,0,0,1-.8,1.81c-.23.43-.5.88-.79,1.34H203.66c-3.19-4.86-2.7-7.67,3.94-7.63q23.15.15,46.26,0c.52,0,1,0,1.44,0h.22c.38,0,.73.08,1,.14a9,9,0,0,1,1,.24l.25.1.31.14a1,1,0,0,1,.3.16.76.76,0,0,1,.19.12,2.25,2.25,0,0,1,.3.24,1.36,1.36,0,0,1,.12.12,2.12,2.12,0,0,1,.29.38,1.2,1.2,0,0,1,.11.23.3.3,0,0,1,0,.1,1.12,1.12,0,0,1,.08.26A2.67,2.67,0,0,1,259.54,251.51Zm19.2-4.85a12.26,12.26,0,0,1-12-11.75,12.53,12.53,0,0,1,11.57-12.29c6.47-.26,12.5,5.65,12.43,12.19A12.33,12.33,0,0,1,278.74,246.66Zm13.61-52.42c0,8.2-1.23,9.35-9.72,9.37q-25.83,0-51.66,0c-17.7,0-35.4-.16-53.09.1-6.54.09-9-2.54-8.93-8.93.19-17.46.22-34.92,0-52.37-.09-6.88,2.77-9.24,9.48-9.19q52.38.28,104.75,0c6.84,0,9.39,2.61,9.29,9.36C292.19,159.81,292.39,177,292.35,194.24Z" />
      
      <rect fill="#006382" x="83.41" y="345.8" width="287.48" height="84.2" rx="12.74" />
      <text fill="#FFFFFF" x="50%" y="418.54" style="font-size: 78px; font-family: Effra-Regular, Helvetica, sans-serif;" text-anchor="middle">${route}</text>
    </svg>
  `;

  return "data:image/svg+xml;base64," + window.btoa(busSvg);
};
